import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ShieldCheck, UserPlus, Trash2, Crown, DollarSign, Calendar, Plus, Loader2,
  Users, Tag, Save, Zap, Globe, Copy, Activity, PlayCircle, CheckCircle2, XCircle, Send,
} from "lucide-react";

const inviteExpiry = (m) => {
  const exp = m.invite_expires_at || (m.invited_at && new Date(new Date(m.invited_at).getTime() + 7 * 864e5).toISOString());
  if (!exp) return null;
  const days = Math.ceil((new Date(exp) - Date.now()) / 864e5);
  return days <= 0 ? { expired: true, label: "Expired" } : { expired: false, label: `Expires in ${days} day${days === 1 ? "" : "s"}` };
};
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PastRoommates } from "../components/PastRoommates";
import { FixedBillsManager } from "../components/FixedBills";
import { CURRENCIES, money, dateFmt } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../components/ui/alert-dialog";

export default function Admin() {
  const qc = useQueryClient();
  const { user, household, loadHousehold } = useAuth();

  const { data: membersData } = useQuery({ queryKey: ["members"], queryFn: async () => (await api.get("/household/members")).data });
  const members = membersData?.members || [];

  const { data: selftestData } = useQuery({ queryKey: ["selftest-runs"], queryFn: async () => (await api.get("/admin/selftest/runs")).data });
  const runs = selftestData?.runs || [];
  const [selftestBusy, setSelftestBusy] = useState(false);

  const runSelftest = async () => {
    setSelftestBusy(true);
    try {
      const { run } = (await api.post("/admin/selftest/run")).data;
      qc.invalidateQueries({ queryKey: ["selftest-runs"] });
      toast[run.passed ? "success" : "error"](run.passed ? "Self-test passed — calculations are correct" : "Self-test FAILED — please review");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not run self-test");
    } finally {
      setSelftestBusy(false);
    }
  };

  const [newEmail, setNewEmail] = useState("");
  const [newName, setNewName] = useState("");
  const [newCat, setNewCat] = useState("");
  const [busy, setBusy] = useState(false);

  const [settlementDay, setSettlementDay] = useState(household?.settlement_day || 30);
  const [hhName, setHhName] = useState(household?.name || "");
  const [currency, setCurrencyState] = useState(household?.currency || "USD");
  const [zelleName, setZelleName] = useState(household?.zelle_name || "");
  const [zelleHandle, setZelleHandle] = useState(household?.zelle_handle || "");
  const [zelleInstr, setZelleInstr] = useState(household?.zelle_instructions || "");
  const [zelleRecipient, setZelleRecipient] = useState(household?.zelle_recipient_user_id || "");
  const [alertSensitivity, setAlertSensitivity] = useState(household?.alert_sensitivity || "standard");

  useEffect(() => {
    if (household) {
      setSettlementDay(household.settlement_day || 30);
      setHhName(household.name || "");
      setCurrencyState(household.currency || "USD");
      setZelleName(household.zelle_name || "");
      setZelleHandle(household.zelle_handle || "");
      setZelleInstr(household.zelle_instructions || "");
      setZelleRecipient(household.zelle_recipient_user_id || "");
      setAlertSensitivity(household.alert_sensitivity || "standard");
    }
  }, [household]);

  const addMember = async () => {
    if (!newEmail.trim()) return toast.error("Enter an email");
    setBusy(true);
    try {
      await api.post("/household/members", { email: newEmail.trim(), name: newName.trim() || null });
      qc.invalidateQueries({ queryKey: ["members"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      toast.success("Invitation sent with a join code");
      setNewEmail("");
      setNewName("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add");
    } finally {
      setBusy(false);
    }
  };

  const removeMember = async (id) => {
    try {
      await api.delete(`/household/members/${id}`);
      qc.invalidateQueries({ queryKey: ["members"] });
      qc.invalidateQueries({ queryKey: ["past-members"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      toast.success("Roommate removed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not remove");
    }
  };

  const [resending, setResending] = useState(null);
  const resendInvite = async (m) => {
    setResending(m.id);
    try {
      await api.post(`/household/members/${m.id}/resend-invite`);
      qc.invalidateQueries({ queryKey: ["members"] });
      toast.success(`Fresh invite sent to ${m.email}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not resend invite");
    } finally {
      setResending(null);
    }
  };

  const makeAdmin = async (id) => {
    try {
      await api.post(`/household/members/${id}/make-admin`);
      await loadHousehold();
      qc.invalidateQueries({ queryKey: ["members"] });
      toast.success("Admin transferred");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not transfer");
    }
  };

  const saveSettings = async () => {
    setBusy(true);
    try {
      await api.put("/household/settings", {
        settlement_day: Number(settlementDay),
        name: hhName,
        currency,
        zelle_name: zelleName, zelle_handle: zelleHandle,
        zelle_instructions: zelleInstr, zelle_recipient_user_id: zelleRecipient || null,
        alert_sensitivity: alertSensitivity,
      });
      await loadHousehold();
      toast.success("Settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  const addCategory = async () => {
    if (!newCat.trim()) return;
    try {
      await api.post("/household/categories", { name: newCat.trim() });
      await loadHousehold();
      toast.success("Category added");
      setNewCat("");
    } catch {
      toast.error("Could not add category");
    }
  };

  const joinedMembers = members.filter((m) => m.user_id && m.approval_status !== "pending");

  const decideJoin = async (m, action) => {
    try {
      await api.post(`/household/members/${m.id}/${action}`);
      qc.invalidateQueries({ queryKey: ["members"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      toast.success(action === "approve" ? `${m.name} approved and added to the split` : `${m.name}'s request declined`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update request");
    }
  };

  const saveCurrency = async (code) => {
    const prev = currency;
    setCurrencyState(code);
    try {
      await api.put("/household/settings", { currency: code });
      await loadHousehold();
      ["dashboard", "expenses", "payments", "history", "settlements"].forEach((k) =>
        qc.invalidateQueries({ queryKey: [k] })
      );
      const label = CURRENCIES.find((c) => c.code === code)?.label || code;
      toast.success(`Portal currency set to ${label}`);
    } catch (e) {
      setCurrencyState(prev);
      toast.error(e.response?.data?.detail || "Could not update currency");
    }
  };

  return (
    <div className="space-y-5 max-w-3xl mx-auto" data-testid="admin-management-panel">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight flex items-center gap-2">
          <ShieldCheck className="h-6 w-6 text-primary" /> Admin
        </h1>
        <p className="text-sm text-muted-foreground mt-0.5">Manage roommates, bills, Zelle & settlement.</p>
      </div>

      {/* Portal currency */}
      <div className="rounded-3xl border border-primary/30 bg-primary/5 p-5" data-testid="portal-currency-card">
        <div className="flex items-center gap-2 mb-1"><Globe className="h-4 w-4 text-primary" /><h2 className="font-heading font-bold">Portal currency</h2></div>
        <p className="text-sm text-muted-foreground mb-3">Choose the currency the whole portal calculates and displays in. Applies instantly to every roommate's dashboard, balances and emails.</p>
        <div className="flex items-center gap-3">
          <Select value={currency} onValueChange={saveCurrency}>
            <SelectTrigger className="h-12 rounded-xl flex-1 bg-card text-base font-semibold" data-testid="portal-currency-select"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-72">{CURRENCIES.map((c) => <SelectItem key={c.code} value={c.code} data-testid={`portal-currency-option-${c.code}`}>{c.label}</SelectItem>)}</SelectContent>
          </Select>
          <div className="h-12 w-12 shrink-0 rounded-xl bg-primary text-primary-foreground flex items-center justify-center font-heading font-bold text-lg" data-testid="currency-preview">{money(1000).replace(/[0-9.,\s]/g, "") || currency}</div>
        </div>
      </div>

      {/* Members */}
      <div className="rounded-3xl border border-border bg-card p-5">
        <div className="flex items-center gap-2 mb-4"><Users className="h-4 w-4 text-primary" /><h2 className="font-heading font-bold">Roommates ({members.length})</h2></div>
        <div className="space-y-2 mb-4">
          {members.map((m) => (
            <div key={m.id} className="flex items-center gap-3 rounded-xl bg-secondary/50 p-3" data-testid="member-row">
              <Avatar className="h-9 w-9"><AvatarFallback className="bg-primary/10 text-primary text-sm font-semibold">{(m.name || m.email)?.[0]?.toUpperCase()}</AvatarFallback></Avatar>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2"><span className="font-medium text-sm truncate">{m.name || m.email}</span>
                  {m.role === "admin" && <Badge className="text-[9px] px-1.5 py-0"><Crown className="h-2.5 w-2.5 mr-0.5" />admin</Badge>}
                  {!m.user_id && <Badge variant="secondary" className="text-[9px] px-1.5 py-0">invited</Badge>}
                  {m.approval_status === "pending" && <Badge className="text-[9px] px-1.5 py-0 whitespace-nowrap bg-amber-500 hover:bg-amber-500" data-testid="pending-approval-badge">waiting approval</Badge>}
                </div>
                <div className="text-xs text-muted-foreground truncate">{m.email}</div>
                {m.approval_status === "pending" && (
                  <div className="flex gap-2 mt-2">
                    <Button size="sm" className="h-8 rounded-lg px-3" onClick={() => decideJoin(m, "approve")} data-testid="approve-member-btn"><CheckCircle2 className="h-4 w-4 mr-1" /> Approve</Button>
                    <Button size="sm" variant="outline" className="h-8 rounded-lg px-3 text-rose-600" onClick={() => decideJoin(m, "reject")} data-testid="reject-member-btn"><XCircle className="h-4 w-4 mr-1" /> Decline</Button>
                  </div>
                )}
                {!m.user_id && m.invite_code && (
                  <div className="text-[11px] mt-0.5 flex items-center gap-1.5" data-testid="member-invite-code">
                    <span className="text-muted-foreground">Code:</span>
                    <code className="font-mono font-bold text-primary tracking-wider">{m.invite_code}</code>
                    <button onClick={() => { navigator.clipboard.writeText(m.invite_code); toast.success("Invite code copied"); }} className="text-muted-foreground hover:text-foreground" data-testid="copy-invite-code-btn"><Copy className="h-3 w-3" /></button>
                    {inviteExpiry(m) && <span className={inviteExpiry(m).expired ? "text-rose-600 font-semibold" : "text-muted-foreground"} data-testid="invite-expiry-label">· {inviteExpiry(m).label}</span>}
                  </div>
                )}
              </div>
              {m.role !== "admin" && (
                <div className="flex gap-1">
                  {!m.user_id && (
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-primary" onClick={() => resendInvite(m)} disabled={resending === m.id} title="Resend invite email" data-testid="resend-invite-btn">
                      {resending === m.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    </Button>
                  )}
                  {m.user_id && m.approval_status !== "pending" && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild><Button size="icon" variant="ghost" className="h-8 w-8" data-testid="make-admin-btn" title="Make admin"><Crown className="h-4 w-4" /></Button></AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader><AlertDialogTitle>Make {m.name} the admin?</AlertDialogTitle><AlertDialogDescription>You will become a regular roommate and lose admin controls.</AlertDialogDescription></AlertDialogHeader>
                        <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => makeAdmin(m.id)}>Transfer admin</AlertDialogAction></AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                  <AlertDialog>
                    <AlertDialogTrigger asChild><Button size="icon" variant="ghost" className="h-8 w-8 text-rose-600" data-testid="remove-member-btn"><Trash2 className="h-4 w-4" /></Button></AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader><AlertDialogTitle>Remove {m.name || m.email}?</AlertDialogTitle><AlertDialogDescription>They'll lose access to this household. Their past expenses remain.</AlertDialogDescription></AlertDialogHeader>
                      <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => removeMember(m.id)} className="bg-rose-600 hover:bg-rose-700">Remove</AlertDialogAction></AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="rounded-xl border border-dashed border-border p-3 space-y-2">
          <div className="flex items-center gap-2 text-sm font-medium"><UserPlus className="h-4 w-4 text-primary" /> Add a roommate by email</div>
          <div className="grid sm:grid-cols-2 gap-2">
            <Input value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="roommate@gmail.com" type="email" className="h-10 rounded-lg" data-testid="member-email-input" />
            <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Name (optional)" className="h-10 rounded-lg" data-testid="member-name-input" />
          </div>
          <Button onClick={addMember} disabled={busy} className="w-full h-10 rounded-lg" data-testid="add-member-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4 mr-1" /> Add roommate</>}</Button>
          <p className="text-[11px] text-muted-foreground">We email them a link and a 6-character invite code. They sign in with Google and enter the code to join.</p>
        </div>
      </div>

      <PastRoommates />

      <FixedBillsManager categories={household?.categories || []} />

      {/* Zelle & settlement */}
      <div className="rounded-3xl border border-border bg-card p-5 space-y-4">
        <div className="flex items-center gap-2"><DollarSign className="h-4 w-4 text-primary" /><h2 className="font-heading font-bold">Household settings</h2></div>

        <div>
          <Label className="text-xs">Household name</Label>
          <Input value={hhName} onChange={(e) => setHhName(e.target.value)} placeholder="e.g. Maple Street Apt 4B" className="mt-1.5 h-11 rounded-xl" data-testid="household-name-setting-input" />
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Zelle recipient name</Label>
            <Input value={zelleName} onChange={(e) => setZelleName(e.target.value)} placeholder="John Smith" className="mt-1.5 h-11 rounded-xl" data-testid="zelle-name-input" />
          </div>
          <div>
            <Label className="text-xs">Zelle email or phone</Label>
            <Input value={zelleHandle} onChange={(e) => setZelleHandle(e.target.value)} placeholder="john@example.com" className="mt-1.5 h-11 rounded-xl font-mono" data-testid="zelle-handle-input" />
          </div>
        </div>
        <div>
          <Label className="text-xs">Payment instructions (optional)</Label>
          <Textarea value={zelleInstr} onChange={(e) => setZelleInstr(e.target.value)} placeholder="e.g. Add your name in the Zelle memo" className="mt-1.5 rounded-xl resize-none" rows={2} data-testid="zelle-instructions-input" />
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Who collects payments?</Label>
            <Select value={zelleRecipient} onValueChange={setZelleRecipient}>
              <SelectTrigger className="mt-1.5 h-11 rounded-xl" data-testid="zelle-recipient-select"><SelectValue placeholder="Select roommate" /></SelectTrigger>
              <SelectContent>{joinedMembers.map((m) => <SelectItem key={m.user_id} value={m.user_id}>{m.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs flex items-center gap-1"><Calendar className="h-3 w-3" /> Monthly settlement day</Label>
            <Select value={String(settlementDay)} onValueChange={(v) => setSettlementDay(Number(v))}>
              <SelectTrigger className="mt-1.5 h-11 rounded-xl" data-testid="settlement-day-select"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-60">{Array.from({ length: 31 }, (_, i) => i + 1).map((d) => <SelectItem key={d} value={String(d)}>{d}{["st", "nd", "rd"][((d % 100) - 20) % 10 - 1] || ["st", "nd", "rd"][d - 1] || "th"} of the month</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label className="text-xs flex items-center gap-1"><Zap className="h-3 w-3" /> AI spending alert sensitivity</Label>
          <Select value={alertSensitivity} onValueChange={setAlertSensitivity}>
            <SelectTrigger className="mt-1.5 h-11 rounded-xl" data-testid="alert-sensitivity-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="off" data-testid="alert-sensitivity-off">Off - no spending alerts</SelectItem>
              <SelectItem value="low" data-testid="alert-sensitivity-low">Low - only clearly wrong expenses</SelectItem>
              <SelectItem value="standard" data-testid="alert-sensitivity-standard">Standard - duplicates and clear outliers</SelectItem>
              <SelectItem value="high" data-testid="alert-sensitivity-high">Strict - flag anything slightly off</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button onClick={saveSettings} disabled={busy} className="w-full h-11 rounded-xl" data-testid="save-settings-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-2" /> Save settings</>}</Button>
      </div>

      {/* Categories */}
      <div className="rounded-3xl border border-border bg-card p-5">
        <div className="flex items-center gap-2 mb-3"><Tag className="h-4 w-4 text-primary" /><h2 className="font-heading font-bold">Expense categories</h2></div>
        <div className="flex flex-wrap gap-2 mb-3">
          {(household?.categories || []).map((c) => <Badge key={c} variant="secondary" className="rounded-full">{c}</Badge>)}
        </div>
        <div className="flex gap-2">
          <Input value={newCat} onChange={(e) => setNewCat(e.target.value)} placeholder="New category name" className="h-10 rounded-lg" data-testid="new-category-input" />
          <Button onClick={addCategory} variant="outline" className="h-10 rounded-lg shrink-0" data-testid="add-category-btn"><Plus className="h-4 w-4 mr-1" /> Add</Button>
        </div>
      </div>

      {/* Calculation self-test */}
      <div className="rounded-3xl border border-border bg-card p-5" data-testid="selftest-card">
        <div className="flex items-center justify-between gap-3 mb-1">
          <div className="flex items-center gap-2"><Activity className="h-4 w-4 text-primary" /><h2 className="font-heading font-bold">Calculation self-test</h2></div>
          <Button onClick={runSelftest} disabled={selftestBusy} size="sm" variant="outline" className="rounded-full shrink-0" data-testid="run-selftest-btn">
            {selftestBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><PlayCircle className="h-4 w-4 mr-1.5" /> Run self-test now</>}
          </Button>
        </div>
        <p className="text-sm text-muted-foreground mb-3">An automated check runs every Sunday and verifies the pro-rata split math on a private sandbox household. Your real data is never touched. Run it now any time to confirm everything adds up.</p>
        {selftestData?.trend?.weeks > 0 && (
          <div className="rounded-xl bg-secondary/50 p-3 mb-3" data-testid="selftest-trend">
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="font-semibold" data-testid="selftest-trend-summary">{selftestData.trend.passed}/{selftestData.trend.weeks} weeks passing</span>
              <span className="text-xs text-muted-foreground" data-testid="selftest-trend-streak">Streak: {selftestData.trend.streak} week{selftestData.trend.streak === 1 ? "" : "s"}</span>
            </div>
            <div className="flex gap-1">
              {[...(selftestData.trend.history || [])].reverse().map((r) => (
                <span key={r.id} className={`h-2 flex-1 rounded-full ${r.passed ? "bg-emerald-500" : "bg-rose-500"}`} title={dateFmt(r.created_at)} />
              ))}
            </div>
          </div>
        )}
        {runs.length === 0 ? (
          <div className="rounded-xl bg-secondary/50 p-4 text-sm text-muted-foreground text-center" data-testid="selftest-empty">No self-test has run yet. Tap "Run self-test now" to try it.</div>
        ) : (
          <div className="space-y-2" data-testid="selftest-runs-list">
            {runs.map((r, idx) => {
              const when = `${dateFmt(r.created_at)} · ${new Date(r.created_at).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`;
              return (
              <div key={r.id} className="rounded-xl border border-border p-3" data-testid="selftest-run-row">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    {r.passed
                      ? <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
                      : <XCircle className="h-4 w-4 text-rose-600 shrink-0" />}
                    <span className={`font-semibold text-sm ${r.passed ? "text-emerald-600" : "text-rose-600"}`}>{r.passed ? "Passed" : "Failed"}</span>
                    <Badge variant="secondary" className="text-[9px] px-1.5 py-0 capitalize">{r.trigger}</Badge>
                  </div>
                  <span className="text-[11px] text-muted-foreground shrink-0">{when}</span>
                </div>
                {idx === 0 && (
                  <div className="mt-2 grid gap-1">
                    {(r.checks || []).map((c, i) => (
                      <div key={i} className="flex items-center gap-1.5 text-[12px] text-muted-foreground">
                        {c.ok ? <CheckCircle2 className="h-3 w-3 text-emerald-600 shrink-0" /> : <XCircle className="h-3 w-3 text-rose-600 shrink-0" />}
                        <span className="truncate">{c.label}</span>
                      </div>
                    ))}
                    {r.error && <div className="text-[12px] text-rose-600 mt-1">Error: {r.error}</div>}
                  </div>
                )}
                {idx !== 0 && r.error && <div className="text-[12px] text-rose-600 mt-1">Error: {r.error}</div>}
              </div>
            );})}
          </div>
        )}
      </div>

      <div className="rounded-2xl bg-primary/5 border border-primary/20 p-4 flex items-start gap-3">
        <Zap className="h-5 w-5 text-primary shrink-0 mt-0.5" />
        <p className="text-xs text-muted-foreground">Bills you add (from the <strong>Add</strong> tab, toggle "Household bill") appear instantly on every roommate's dashboard and are split equally. Settlement emails with Zelle details go out automatically on day {settlementDay}.</p>
      </div>
    </div>
  );
}
